import { PageType, getPageType, isCarvanaUrl } from '../helpers/page-type';

import { isEmpty, get } from 'lodash';
import { axiosWithRetry, formatError } from 'utils/axios';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { getHostname } from 'utils/url';
import { FinancingImageUrl, TradesImageUrl, GenericImage } from '../helpers/assets';
import { cleanString } from '../helpers/string-helpers';
import { getVehicleDetailsById, getVehicleDetailsBySku } from '../helpers/vehicle-details';
import { HTTP_REQUEST_METHODS } from 'constants/http';

const clientHeaders = {
  Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8',
  'Accept-Language': 'en-US;q=0.8,en;q=0.7',
  'Cache-Control': 'max-age=0',
  'Upgrade-Insecure-Requests': '1',
};

class OpenGraph {
  public image = '';

  public description = '';

  public title = '';

  private isCarvana = false;

  private pageType = PageType.Unknown;

  private doc: Document = {} as Document;

  private url = '';

  public load = async (url: string) => {
    try {
      this.pageType = getPageType(url);
      this.isCarvana = isCarvanaUrl(url);
      this.url = url;
      if (this.pageType === PageType.Vdp1) {
        const vehicleSkuRegex = /\/search\/(\d{10})(.+)?/gi;
        const regexResult = vehicleSkuRegex.exec(url);
        if (regexResult !== null) {
          const details = await getVehicleDetailsBySku(regexResult[1]);
          if (details) {
            Object.assign(this, details);
            return;
          }
        }
      }
      if (this.pageType === PageType.Vdp2) {
        const vehicleIdRegex = /\/vehicle\/(\d+)(.+)?/gi;
        const regexResult = vehicleIdRegex.exec(url);
        if (regexResult !== null) {
          const details = await getVehicleDetailsById(regexResult[1]);
          if (details) {
            Object.assign(this, details);
            return;
          }
        }
      }
      if (!url.endsWith('.pdf')) {
        try {
          const req = await axiosWithRetry({
            headers: clientHeaders,
            method: HTTP_REQUEST_METHODS.GET,
            url,
            retries: 0,
          });

          const domParser = new DOMParser();
          const documentFromUrl = domParser.parseFromString(req.data, 'text/html');
          this.doc = documentFromUrl;
          const title =
            documentFromUrl.querySelector("meta[property='og:title']")?.getAttribute('content') ??
            '';
          const descr =
            documentFromUrl
              .querySelector("meta[property='og:description']")
              ?.getAttribute('content') ?? '';
          let image =
            documentFromUrl.querySelector("meta[property='og:image']")?.getAttribute('content') ??
            '';

          if (!getHostname(image)) {
            // The CDN does not play nice with double slashes `//` in the path
            image = image.startsWith('/')
              ? `${process.env.CVNA_FASTLY_CDN_BASE_URL}${image.slice(1)}`
              : `${process.env.CVNA_FASTLY_CDN_BASE_URL}${image}`;
          }

          this.title = cleanString(title);
          this.description = cleanString(descr);
          this.image = cleanString(image);
        } catch (e) {
          const axiosError = formatError(e);
          const requestStatus = get(axiosError, 'config.status', null);
          const requestUrl = get(axiosError, 'config.url', '');

          // A failed resource fetch should return some HTTP requestStatus. Likely encountered
          // CORS rejection from external website. Not possible to pinpoint CORS error programmatically like browser can.
          // Ref: https://stackoverflow.com/questions/19325314/how-to-detect-cross-origin-cors-error-vs-other-types-of-errors-for-xmlhttpreq
          if (!requestStatus && requestUrl) {
            DataDogLogger.action(CUSTOM_ACTION_NAMES.INFORMATION_CARD_GENERATOR.CORS_REJECTION, {
              href: requestUrl,
              origin: new URL(requestUrl).origin,
              rejectCode: get(axiosError, 'code', ''),
              rejectMessage: get(axiosError, 'message', ''),
            });
          } else {
            DataDogLogger.error(
              new Error(CUSTOM_ACTION_NAMES.INFORMATION_CARD_GENERATOR.ERROR),
              axiosError,
            );
            console.warn({ info: 'Failed to retrieve url' });
          }
        }
      }
    } catch (e) {
      DataDogLogger.error(
        new Error(CUSTOM_ACTION_NAMES.INFORMATION_CARD_GENERATOR.ERROR),
        formatError(e),
      );
      console.error({ message: (e as Error).message });
      this.fallbacks();
    }
    this.fallbacks();
  };

  private titleFallback = () => {
    try {
      if (!isEmpty(this.doc)) {
        const titleContents = this.doc.querySelector('title')?.text;
        if (titleContents) {
          this.title = titleContents;
          return;
        }
      }
      switch (this.pageType) {
        case PageType.WindowSticker:
          this.title = 'Original Manufacturer Window Sticker';
          return;

        case PageType.PdfDocument:
          this.title = 'PDF Document';
          return;

        case PageType.Search:
        case PageType.MobileSearch:
          this.title = 'Used Cars For Sale | Carvana';
          return;
        default:
          break;
      }

      const domainMatch = this.url.match(/^https?\\:\/\/([^\\/?#]+)(?:[\\/?#]|$)/i);
      const domain = domainMatch && domainMatch[1];
      this.title = domain !== null ? domain : 'Link';
    } catch (e) {
      DataDogLogger.error(
        new Error(CUSTOM_ACTION_NAMES.INFORMATION_CARD_GENERATOR.FB_TITLE_ERROR),
        e,
      );

      console.error({
        info: 'Error generating fallback title',
        message: (e as Error).message,
      });
      this.title = 'Link';
    }
  };

  private imageFallback = () => {
    switch (this.pageType) {
      case PageType.Trades:
        this.image = TradesImageUrl;
        return;
      case PageType.Vdp2: {
        this.image = GenericImage;
        return;
      }
      case PageType.WindowSticker: {
        this.image = GenericImage;
        return;
      }
      case PageType.Financing:
        this.image = FinancingImageUrl;
        return;

      default:
        break;
    }

    if (!this.image) {
      this.image = GenericImage;
    }
  };

  private descriptionFallback = () => {
    try {
      if (!isEmpty(this.doc)) {
        const metaDescription = this.doc
          .querySelector('meta[name=description]')
          ?.getAttribute('content');
        if (metaDescription) {
          this.description = cleanString(metaDescription);
        }
      }
      switch (this.pageType) {
        case PageType.Vdp2:
          this.description = 'View Vehicle Details.';
          return;
        case PageType.OrderPlacedDashboard:
          this.description = 'View your Order Dashboard.';
          return;
        case PageType.WindowSticker:
          this.description = 'Click to view the original manufacturer window sticker.';
          return;
        case PageType.Search:
          this.description =
            'Search for your next car using Carvana. Browse used cars online that are available to be delivered to your door. Carvana is the new way to buy a car.';
          return;

        case PageType.Financing:
          this.description =
            'Get prequalified for a Carvana auto loan in minutes. You can browse our expansive inventory of used cars with personalized financing terms without impacting your credit score.';
          return;

        default:
          break;
      }

      if (!this.description) {
        this.description = `Click to view details.\n${this.url}`;
      }
    } catch (e) {
      DataDogLogger.error(
        new Error(CUSTOM_ACTION_NAMES.INFORMATION_CARD_GENERATOR.FB_DESCRIPTION_ERROR),
        e,
      );
      console.error({
        info: 'Error generating fallback description',
        message: (e as Error).message,
      });
      this.description = `Click to view details.\n${this.url}`;
    }
  };

  private fallbacks = () => {
    if (!this.title) {
      this.titleFallback();
    }
    if (!this.image) {
      this.imageFallback();
    }
    if (!this.description) {
      this.descriptionFallback();
    }
  };
}

export { OpenGraph };
